import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API = process.env.REACT_APP_API_URL || '';
function authHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// Slash-menu block options — each applies a Quill format to the current line.
const SLASH_ITEMS = [
  { key: 'h1', label: 'Heading 1', hint: 'Large section title', apply: (q, r) => q.formatLine(r.index, 1, 'header', 1) },
  { key: 'h2', label: 'Heading 2', hint: 'Medium section title', apply: (q, r) => q.formatLine(r.index, 1, 'header', 2) },
  { key: 'text', label: 'Text', hint: 'Plain paragraph', apply: (q, r) => q.formatLine(r.index, 1, 'header', false) },
  { key: 'bullet', label: 'Bulleted list', hint: '• item', apply: (q, r) => q.formatLine(r.index, 1, 'list', 'bullet') },
  { key: 'ordered', label: 'Numbered list', hint: '1. item', apply: (q, r) => q.formatLine(r.index, 1, 'list', 'ordered') },
  { key: 'check', label: 'To-do list', hint: 'Checkbox items', apply: (q, r) => q.formatLine(r.index, 1, 'list', 'unchecked') },
  { key: 'quote', label: 'Quote', hint: 'Callout / blockquote', apply: (q, r) => q.formatLine(r.index, 1, 'blockquote', true) },
  { key: 'code', label: 'Code block', hint: 'Monospace block', apply: (q, r) => q.formatLine(r.index, 1, 'code-block', true) },
  { key: 'divider', label: 'Divider', hint: 'Horizontal rule', apply: (q, r) => { q.insertText(r.index, '\n'); q.formatLine(r.index, 1, 'divider', true); } },
];

export default function MappingNotes({ pipelineId }) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [savedAt, setSavedAt] = useState(null);
  const quillRef = useRef(null);
  const saveTimer = useRef(null);
  const loadedRef = useRef(false);

  // Slash menu state.
  const [slash, setSlash] = useState(null); // { top, left, index } | null
  const [slashQuery, setSlashQuery] = useState('');
  const [slashActive, setSlashActive] = useState(0);

  // Load existing notes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/research/${pipelineId}/notes`, { headers: authHeaders() });
        const data = await r.json();
        if (cancelled) return;
        setValue(data.content || '');
        setSavedAt(data.updated_at || null);
      } catch (e) {
        console.error('notes load failed', e);
      } finally {
        if (!cancelled) { setLoading(false); loadedRef.current = true; }
      }
    })();
    return () => { cancelled = true; };
  }, [pipelineId]);

  // Debounced auto-save.
  const scheduleSave = useCallback((html) => {
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/research/${pipelineId}/notes`, {
          method: 'PUT', headers: authHeaders(), body: JSON.stringify({ content: html }),
        });
        const data = await r.json();
        setSavedAt(data.updated_at || new Date().toISOString());
        setSaveState('saved');
      } catch (e) {
        console.error('notes save failed', e);
        setSaveState('idle');
      }
    }, 800);
  }, [pipelineId]);

  const onChange = (html) => {
    setValue(html);
    if (loadedRef.current) scheduleSave(html);
  };

  // Detect "/" at line start to open the slash menu.
  const handleKeyUp = useCallback((e) => {
    const q = quillRef.current?.getEditor?.();
    if (!q) return;
    const sel = q.getSelection();
    if (!sel) { setSlash(null); return; }

    if (slash) {
      // Update the query as the user types after "/".
      const lineText = q.getText(slash.index, sel.index - slash.index);
      if (e.key === 'Escape' || lineText.includes(' ')) { setSlash(null); return; }
      setSlashQuery(lineText.replace(/^\//, ''));
      return;
    }

    if (e.key === '/') {
      const bounds = q.getBounds(sel.index);
      setSlash({ top: bounds.top + bounds.height, left: bounds.left, index: sel.index - 1 });
      setSlashQuery('');
      setSlashActive(0);
    }
  }, [slash]);

  const filteredSlash = useMemo(() => {
    const ql = slashQuery.toLowerCase();
    return SLASH_ITEMS.filter(i => !ql || i.label.toLowerCase().includes(ql));
  }, [slashQuery]);

  const applySlash = useCallback((item) => {
    const q = quillRef.current?.getEditor?.();
    if (!q || !slash) return;
    // Remove the "/query" text, then apply the block format.
    const removeLen = slashQuery.length + 1;
    q.deleteText(slash.index, removeLen);
    const r = { index: slash.index, length: 0 };
    q.setSelection(r.index, 0);
    item.apply(q, r);
    setSlash(null);
    setSlashQuery('');
  }, [slash, slashQuery]);

  // Keyboard nav inside the slash menu.
  const handleKeyDown = useCallback((e) => {
    if (!slash) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSlashActive(a => Math.min(a + 1, filteredSlash.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSlashActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filteredSlash[slashActive]) applySlash(filteredSlash[slashActive]); }
    else if (e.key === 'Escape') { setSlash(null); }
  }, [slash, filteredSlash, slashActive, applySlash]);

  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      ['blockquote', 'code-block'],
      [{ color: [] }, { background: [] }],
      ['link'],
      ['clean'],
    ],
  }), []);

  const relTime = (iso) => {
    if (!iso) return '';
    const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return 'just now';
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-4 h-11 flex items-center justify-between border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Search Notes</h3>
        <span className="text-[11px] text-gray-400">
          {saveState === 'saving' ? 'Saving…' : savedAt ? `Saved ${relTime(savedAt)}` : ''}
        </span>
      </div>

      {/* Editor */}
      <div className="flex-1 min-h-0 relative mapping-notes" onKeyUp={handleKeyUp} onKeyDown={handleKeyDown}>
        {loading ? (
          <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600" /></div>
        ) : (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            placeholder="Strategy, market notes, questions for the client… Type / for blocks."
          />
        )}

        {/* Slash command menu */}
        {slash && filteredSlash.length > 0 && (
          <div className="absolute z-30 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto"
            style={{ top: slash.top + 44, left: Math.min(slash.left + 12, 200) }}>
            {filteredSlash.map((item, idx) => (
              <button key={item.key}
                onMouseEnter={() => setSlashActive(idx)}
                onClick={() => applySlash(item)}
                className={`w-full text-left px-3 py-1.5 flex flex-col ${idx === slashActive ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                <span className="text-xs font-medium text-gray-800">{item.label}</span>
                <span className="text-[10px] text-gray-400">{item.hint}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .mapping-notes .quill { height: 100%; display: flex; flex-direction: column; }
        .mapping-notes .ql-container { flex: 1; overflow-y: auto; font-size: 13px; border: none; }
        .mapping-notes .ql-toolbar { border: none; border-bottom: 1px solid #e5e7eb; position: sticky; top: 0; background: #fff; z-index: 2; }
        .mapping-notes .ql-editor { min-height: 100%; }
        .mapping-notes .ql-editor.ql-blank::before { color: #9ca3af; font-style: normal; }
        .mapping-notes blockquote { border-left: 3px solid #10b981; background: #f0fdf4; padding: 6px 12px; color: #374151; }
      `}</style>
    </div>
  );
}
